import { parseZonedDateTime } from '@rematter/common/dateTime';
import { atom, getDefaultStore, Getter } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import keyBy from 'lodash/keyBy';

import { fileUrlQueryAtom } from '~/modules/trade/_graphql/queryFileUrl';
import {
  OptimisticUpdateStatus,
  PhotoOptimisticState,
} from '~/modules/trade/types/photo';
import { ParameterizedQueryAtom } from '~/shared/providers/api/createQueryAtomFactory';

export type { OptimisticUpdateStatus, PhotoOptimisticState };

export type OptimisticPhotoUpdate = PhotoOptimisticState & {
  data?: Record<string, unknown>; // For updates (new values)
  previousData: Record<string, unknown>; // For rollback/display
};

// Track optimistic updates by photo ID
export const $optimisticPhotoUpdates = atom<Map<string, OptimisticPhotoUpdate>>(
  new Map(),
);

// Apply an optimistic update (status: pending)
export const $applyOptimisticUpdate = atom(
  null,
  (
    get,
    set,
    params: {
      id: string;
      type: 'update' | 'delete';
      data?: Record<string, unknown>;
      previousData: Record<string, unknown>;
    },
  ) => {
    const current = get($optimisticPhotoUpdates);
    const next = new Map(current);
    next.set(params.id, { ...params, status: 'pending' });
    set($optimisticPhotoUpdates, next);
  },
);

// Mark an optimistic update as error
export const $markOptimisticUpdateError = atom(
  null,
  (
    get,
    set,
    params: {
      id: string;
      error: string;
    },
  ) => {
    const current = get($optimisticPhotoUpdates);
    const existing = current.get(params.id);
    if (existing) {
      const next = new Map(current);
      next.set(params.id, {
        ...existing,
        status: 'error',
        error: params.error,
      });
      set($optimisticPhotoUpdates, next);
    }
  },
);

// Remove an optimistic update (on success or dismiss)
export const $removeOptimisticUpdate = atom(null, (get, set, id: string) => {
  const current = get($optimisticPhotoUpdates);
  if (current.has(id)) {
    const next = new Map(current);
    next.delete(id);
    set($optimisticPhotoUpdates, next);
  }
});

// ==========================================
// File URL Generation
// ==========================================

export const $generateFilesUrls = fileUrlQueryAtom();

export const filesWithUrlsAtom = <TVariables>(
  getConfig: (get: Getter) => {
    variables: TVariables;
    skip?: boolean;
    photosAtom: ParameterizedQueryAtom<
      TVariables,
      {
        id: string;
        fileName: string;
        fileSize: number;
        mimeType: string;
        status: string;
        metadata: Record<string, unknown> | null | undefined;
        timestamps: {
          createdAt: string;
          updatedAt: string;
        };
      }[],
      {
        id: string;
        fileName: string;
        fileSize: number;
        mimeType: string;
        status: string;
        metadata: Record<string, unknown> | null | undefined;
        timestamps: {
          createdAt: string;
          updatedAt: string;
        };
      }[]
    >;
  },
) =>
  atomWithQuery((get) => {
    const config = getConfig(get);
    return {
      queryKey: ['loadPhotosWithUrls', config.variables],
      queryFn: async () => {
        const store = getDefaultStore();

        // Always fetch fresh data to ensure we get the latest photos after uploads
        const photosConfig = get(config.photosAtom);
        const photos = await photosConfig.fetch(config.variables);

        const filesWithUrls =
          photos && photos.length > 0
            ? await store.set($generateFilesUrls, {
                fileIds: photos.map((it) => it.id),
              })
            : [];

        const urlsByFileId = keyBy(filesWithUrls, (it) => it.fileId);

        const mappedPhotos =
          photos?.map((photo) => ({
            id: photo.id,
            url: urlsByFileId[photo.id]?.url ?? '',
            fileName: photo.fileName,
            fileSize: photo.fileSize,
            mimeType: photo.mimeType,
            status: photo.status,
            metadata: photo.metadata,
            createdAt: parseZonedDateTime(photo.timestamps.createdAt),
            updatedAt: parseZonedDateTime(photo.timestamps.updatedAt),
          })) ?? [];

        // Sort by createdAt ascending (oldest first)
        return mappedPhotos.sort((a, b) => a.createdAt.compare(b.createdAt));
      },
      enabled: !config.skip,
    };
  });
